import * as React from "react";
import { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import useWorkflows from "@/hooks/useWorkflows";
import useRules from "@/hooks/useRules";
import useChatWindows from "@/hooks/useChatWindows";
import useTrackEvent from "@/hooks/useTrackEvent";

type StageColor = "blue" | "amber" | "emerald" | "violet" | "slate";

type StageLink = {
  label: string;
  path: string;
};

type Stage = {
  number: number;
  name: string;
  explanation: string;
  whatToCreate: string;
  links: StageLink[];
  color: StageColor;
  workflowCount?: number;
  ruleCount?: number;
  windowCount?: number;
};

const colorClasses: Record<StageColor, { bg: string; border: string; text: string; badge: string; num: string; link: string }> = {
  blue: {
    bg: "bg-blue-50",
    border: "border-blue-200",
    text: "text-blue-900",
    badge: "bg-blue-100 text-blue-800",
    num: "bg-blue-600 text-white",
    link: "text-blue-700 hover:text-blue-900"
  },
  amber: {
    bg: "bg-amber-50",
    border: "border-amber-200",
    text: "text-amber-900",
    badge: "bg-amber-100 text-amber-800",
    num: "bg-amber-600 text-white",
    link: "text-amber-700 hover:text-amber-900"
  },
  emerald: {
    bg: "bg-emerald-50",
    border: "border-emerald-200",
    text: "text-emerald-900",
    badge: "bg-emerald-100 text-emerald-800",
    num: "bg-emerald-600 text-white",
    link: "text-emerald-700 hover:text-emerald-900"
  },
  violet: {
    bg: "bg-violet-50",
    border: "border-violet-200",
    text: "text-violet-900",
    badge: "bg-violet-100 text-violet-800",
    num: "bg-violet-600 text-white",
    link: "text-violet-700 hover:text-violet-900"
  },
  slate: {
    bg: "bg-slate-50",
    border: "border-slate-200",
    text: "text-slate-900",
    badge: "bg-slate-200 text-slate-800",
    num: "bg-slate-600 text-white",
    link: "text-slate-700 hover:text-slate-900"
  }
};

function CountBadge({ count, label, color }: { count: number; label: string; color: StageColor }) {
  const c = colorClasses[color];
  if (count === 0) return null;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${c.badge}`}>
      {count} {count === 1 ? label.replace(/s$/, "") : label}
    </span>
  );
}

export default function WorkflowsJourneyView() {
  const { workflows, fetch: fetchWorkflows } = useWorkflows();
  const { rules, refresh: refreshRules } = useRules();
  const { chatWindows } = useChatWindows();
  const { track } = useTrackEvent();

  useEffect(() => {
    track("feature_click", "Workflows/JourneyView/open");
    fetchWorkflows();
    refreshRules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stages: Stage[] = useMemo(() => {
    const safeWorkflows = Array.isArray(workflows) ? workflows.filter((w) => !w.isTemplate) : [];
    const safeRules = Array.isArray(rules) ? rules : [];
    const safeWindows = Array.isArray(chatWindows) ? chatWindows : [];

    return [
      {
        number: 1,
        name: "Page loads",
        explanation: "The visitor lands on a page with your chat widget. Decide whether the widget appears, and which chat window design to show.",
        whatToCreate: "Create a workflow with the 'Widget Loads on Page' trigger. Use the Hide Widget or Select Chat Window action.",
        links: [{ label: "Open Workflows →", path: "/workflows" }],
        color: "blue",
        workflowCount: safeWorkflows.filter((w) => w.triggerType === "widget_loaded" && w.enabled).length,
        ruleCount: safeRules.filter((r) => r.trigger === "determine-chat-window").length,
      },
      {
        number: 2,
        name: "Button clicked",
        explanation: "The visitor clicks the chat button. Customize the pre-chat form or show a specific chat window before the conversation opens.",
        whatToCreate: "Create a workflow with the 'Chat Button Clicked' trigger. Use the Select Pre-Chat action to show a form.",
        links: [{ label: "Open Workflows →", path: "/workflows" }],
        color: "amber",
        workflowCount: safeWorkflows.filter((w) => w.triggerType === "chat_button_clicked" && w.enabled).length,
        windowCount: safeWindows.length,
      },
      {
        number: 3,
        name: "Conversation starts",
        explanation: "A new conversation opens. Route it to a bot, a team, or a specific agent based on any condition.",
        whatToCreate: "Create a workflow with the 'Conversation Opened' trigger. Use Transfer to AI or Transfer to Agents.",
        links: [{ label: "Open Workflows →", path: "/workflows" }],
        color: "emerald",
        workflowCount: safeWorkflows.filter((w) => w.triggerType === "conversation_opened" && w.enabled).length,
      },
      {
        number: 4,
        name: "During chat",
        explanation: "The conversation is live. Handle the case when no agents are available, or show a fallback experience.",
        whatToCreate: "Go to Rules and create an 'Agent Unavailable' rule to show a workflow or message when no one is online.",
        links: [{ label: "Open Rules →", path: "/rules" }],
        color: "violet",
        ruleCount: safeRules.filter(
          (r) => r.trigger === "use-workflow-on-unavailable" || r.trigger === "use-unavailable-survey" || r.trigger === "use-ai"
        ).length,
      },
      {
        number: 5,
        name: "After chat",
        explanation: "The conversation ends. Send a survey, trigger a follow-up workflow, or auto-resolve based on conditions.",
        whatToCreate: "Go to Rules and create a 'Post-Chat Survey' or 'On Resolve' rule.",
        links: [{ label: "Open Rules →", path: "/rules" }],
        color: "slate",
        ruleCount: safeRules.filter(
          (r) =>
            r.trigger === "use-postchat-survey" ||
            r.trigger === "use-workflow-on-resolve" ||
            r.trigger === "resolve-chat"
        ).length,
      }
    ];
  }, [workflows, rules, chatWindows]);

  return (
    <div className="px-4 py-4">
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3 xl:gap-2 relative">
        {stages.map((stage, idx) => {
          const c = colorClasses[stage.color];
          const isLast = idx === stages.length - 1;
          const hasAnything = (stage.workflowCount ?? 0) + (stage.ruleCount ?? 0) + (stage.windowCount ?? 0) > 0;

          return (
            <div key={stage.number} className="relative">
              <div className={`${c.bg} ${c.border} border rounded-lg p-4 h-full flex flex-col`}>
                {/* Header */}
                <div className="flex items-center gap-2 mb-2">
                  <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold shrink-0 ${c.num}`}>
                    {stage.number}
                  </span>
                  <span className={`text-sm font-semibold ${c.text}`}>{stage.name}</span>
                </div>

                {/* Explanation */}
                <p className={`text-xs leading-relaxed ${c.text} opacity-80 flex-1`}>{stage.explanation}</p>

                {/* Counts */}
                {hasAnything ? (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {stage.workflowCount != null && stage.workflowCount > 0 && (
                      <CountBadge count={stage.workflowCount} label="workflows" color={stage.color} />
                    )}
                    {stage.ruleCount != null && stage.ruleCount > 0 && (
                      <CountBadge count={stage.ruleCount} label="rules" color={stage.color} />
                    )}
                    {stage.windowCount != null && stage.windowCount > 0 && (
                      <CountBadge count={stage.windowCount} label="windows" color={stage.color} />
                    )}
                  </div>
                ) : (
                  <div className="mt-3">
                    <p className={`text-[11px] italic opacity-60 ${c.text}`}>{stage.whatToCreate}</p>
                  </div>
                )}

                {/* Links */}
                <div className="mt-3 pt-3 border-t border-black/5 flex flex-col gap-1">
                  {stage.links.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      className={`text-xs font-semibold ${c.link} inline-flex items-center gap-1 underline-offset-2 hover:underline`}
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              </div>

              {!isLast && (
                <div
                  className="hidden xl:flex absolute top-1/2 -right-2 -translate-y-1/2 z-10 items-center justify-center w-4 h-4 text-slate-400"
                  aria-hidden="true"
                >
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
